import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Target, Zap, GitFork, BookMarked, Award, ArrowRight } from 'lucide-react';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg border border-paper-300 bg-paper-100 shadow-2xl dark:border-study-line dark:bg-study-panel"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-paper-300 bg-paper-100/95 px-6 py-4 backdrop-blur dark:border-study-line dark:bg-study-panel/95">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800 dark:bg-teal-950/60 dark:text-teal-300">
                Guia Metodológico
              </span>
              <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">IFF Campos Centro</span>
            </div>
            <h2 className="mt-1 font-serif text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Como Funciona o Portal do Vestibulando
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar guia"
            className="rounded p-1.5 text-zinc-400 hover:bg-paper-200 hover:text-zinc-700 dark:hover:bg-study-card dark:hover:text-zinc-200 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="space-y-6 p-6 text-xs text-zinc-700 dark:text-zinc-300 leading-relaxed font-sans">
          {/* Summary Box */}
          <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-4 dark:border-teal-900/40 dark:bg-teal-950/20">
            <p className="text-xs font-serif leading-relaxed text-teal-950 dark:text-teal-200">
              O Portal do Vestibulando é um ambiente de treino deliberado projetado para superar a prova do IFF. 
              Em vez de resolver simulados passivamente, o foco é dominar a <strong>triagem rápida em até 3 minutos</strong>: 
              decodificar o enunciado, identificar o gatilho teórico e traçar a rota resolutiva antes de gastar energia com cálculos.
            </p>
          </div>

          {/* Section 1: Pesos Estratégicos */}
          <div className="border-t border-paper-200 pt-4 dark:border-study-line">
            <h3 className="flex items-center gap-2 font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              <Award className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              1. A Estratégia de Pesos do IFF
            </h3>
            <p className="mt-1 text-zinc-600 dark:text-zinc-400">
              A nota de corte dos cursos mais concorridos é matematicamente dominada pelos pesos do edital:
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 font-mono">
              <div className="rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/30">
                <span className="block text-[11px] font-bold text-amber-800 dark:text-amber-300">
                  Matemática
                </span>
                <span className="text-xl font-bold text-amber-900 dark:text-amber-100">PESO 5</span>
                <span className="block text-[10px] text-zinc-500 mt-1">10 questões = 50 pontos</span>
              </div>
              <div className="rounded border border-teal-300 bg-teal-50 p-3 dark:border-teal-900/50 dark:bg-teal-950/30">
                <span className="block text-[11px] font-bold text-teal-800 dark:text-teal-300">
                  Ciências da Natureza
                </span>
                <span className="text-xl font-bold text-teal-900 dark:text-teal-100">PESO 2</span>
                <span className="block text-[10px] text-zinc-500 mt-1">Física, Química, Biologia</span>
              </div>
              <div className="rounded border border-paper-300 bg-paper-50 p-3 dark:border-study-line dark:bg-study-card">
                <span className="block text-[11px] font-bold text-zinc-600 dark:text-zinc-400">
                  Linguagens e Humanas
                </span>
                <span className="text-xl font-bold text-zinc-800 dark:text-zinc-200">PESO 1</span>
                <span className="block text-[10px] text-zinc-500 mt-1">Português, História, Geografia</span>
              </div>
            </div>
          </div>

          {/* Section 2: O Ciclo de Triagem */}
          <div className="border-t border-paper-200 pt-4 dark:border-study-line">
            <h3 className="flex items-center gap-2 font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              2. O Ciclo de Triagem Rápida (Até 3 minutos)
            </h3>
            <p className="mt-1 text-zinc-600 dark:text-zinc-400">
              Durante a triagem, você é avaliado pela velocidade de estruturação mental. Os campos adaptam-se à disciplina da questão:
            </p>
            <div className="mt-3 space-y-2.5">
              <div className="rounded border border-paper-200 bg-paper-50 p-3 dark:border-study-line dark:bg-study-card">
                <span className="font-bold text-zinc-900 dark:text-zinc-100">Para Matemática e Física (Exatas):</span>
                <ul className="mt-1 list-disc list-inside space-y-0.5 text-zinc-600 dark:text-zinc-400 text-[11px]">
                  <li><strong>Dados Fornecidos:</strong> Extração das grandezas físicas ou coeficientes do problema.</li>
                  <li><strong>Incógnita Alvo:</strong> A variável exata que deve ser calculada.</li>
                  <li><strong>Gatilho / Teoria:</strong> O modelo conceitual ativado pelas palavras do texto.</li>
                  <li><strong>Fórmula-Ponte Bruta:</strong> A equação direta que conecta os dados à incógnita.</li>
                </ul>
              </div>
              <div className="rounded border border-paper-200 bg-paper-50 p-3 dark:border-study-line dark:bg-study-card">
                <span className="font-bold text-zinc-900 dark:text-zinc-100">Para Linguagens, Humanas e Biologia Conceitual:</span>
                <ul className="mt-1 list-disc list-inside space-y-0.5 text-zinc-600 dark:text-zinc-400 text-[11px]">
                  <li>Não exige fórmulas nem entradas numéricas artificiais.</li>
                  <li>Foco direto no <strong>Gatilho Conceitual / Tese Central</strong> e no <strong>Comando / Objeto de Análise</strong>.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section 3: Grafo de Fórmulas e Rota BFS */}
          <div className="border-t border-paper-200 pt-4 dark:border-study-line">
            <h3 className="flex items-center gap-2 font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              <GitFork className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              3. O Grafo Hipergeométrico de Fórmulas
            </h3>
            <p className="mt-1 text-zinc-600 dark:text-zinc-400">
              O módulo de Grafo modela o conjunto de fórmulas como uma rede de dependências. Ao selecionar as variáveis que o enunciado fornece e a variável que se busca, o algoritmo de busca em largura (BFS) descobre a rota com menor número de passos analíticos intermediários.
            </p>
          </div>

          {/* Section 4: Caderno de Erros e Diagnóstico de Causa-Raiz */}
          <div className="border-t border-paper-200 pt-4 dark:border-study-line">
            <h3 className="flex items-center gap-2 font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              <BookMarked className="h-4 w-4 text-rose-600 dark:text-rose-400" />
              4. Diagnóstico de Falhas (Caderno de Erros)
            </h3>
            <p className="mt-1 text-zinc-600 dark:text-zinc-400">
              Errar faz parte do treino deliberado, mas só gera evolução se a causa-raiz for isolada:
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
              <div className="rounded border border-red-200 bg-red-50/50 p-2.5 dark:border-red-950 dark:bg-red-950/20">
                <span className="font-bold text-red-900 dark:text-red-300">Falha de Decodificação</span>
                <p className="text-zinc-600 dark:text-zinc-400 mt-0.5">Não identificou qual matéria ou fenômeno estava sendo cobrado.</p>
              </div>
              <div className="rounded border border-amber-200 bg-amber-50/50 p-2.5 dark:border-amber-950 dark:bg-amber-950/20">
                <span className="font-bold text-amber-900 dark:text-amber-300">Falha de Memória</span>
                <p className="text-zinc-600 dark:text-zinc-400 mt-0.5">Identificou o fenômeno, mas esqueceu a fórmula ou relação exata.</p>
              </div>
              <div className="rounded border border-blue-200 bg-blue-50/50 p-2.5 dark:border-blue-950 dark:bg-blue-950/20">
                <span className="font-bold text-blue-900 dark:text-blue-300">Falha de Execução</span>
                <p className="text-zinc-600 dark:text-zinc-400 mt-0.5">Sabia a fórmula correta, mas errou simplificação ou conversão de unidades.</p>
              </div>
              <div className="rounded border border-teal-200 bg-teal-50/50 p-2.5 dark:border-teal-950 dark:bg-teal-950/20">
                <span className="font-bold text-teal-900 dark:text-teal-300">Falha de Leitura</span>
                <p className="text-zinc-600 dark:text-zinc-400 mt-0.5">Desatenção a pegadinhas, 'exceto', 'incorreto' ou unidades dadas.</p>
              </div>
            </div>
          </div>

          {/* Section 5: Matriz de Referência Anexo VI */}
          <div className="border-t border-paper-200 pt-4 dark:border-study-line">
            <h3 className="flex items-center gap-2 font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              <Target className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              5. Descritores Oficiais Anexo VI
            </h3>
            <p className="mt-1 text-zinc-600 dark:text-zinc-400">
              Todas as 250 questões das últimas provas do IFF (e novas provas em PDF adicionadas) são indexadas algoritmicamente contra os descritores das Matrizes de Referência (Língua Portuguesa D1–D21, Matemática D1–D37, Física, Química e Biologia). Isso permite auditar com precisão quais habilidades exigem reforço.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-paper-300 bg-paper-50 px-6 py-3 dark:border-study-line dark:bg-study-panel/50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 rounded bg-zinc-900 px-4 py-2 text-xs font-medium text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white transition"
          >
            Fechar
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
